import React, { useEffect, useMemo, useState } from "react";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { toast } from "react-toastify";
import GlobalDelete from "../../../components/common/GlobalDelete";
import fetchAllCentersWithIds from "../../List/CenterList";
import api from "../../../config/URL";
import { exportVisibleColumnsToCSV } from "../../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../../utility/mrtColumnPersistence";

const MODULE_KEY = "PAYROLL";

const Payroll = ({ selectedCenter }) => {
  // const [filters, setFilters] = useState({
  //   centerName: "",
  //   employeeName: "",
  //   roll: "",
  // });
  const navigate = useNavigate();
  const [datas, setDatas] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [centerData, setCenterData] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  /* -------------------- ROLE MATRIX -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canIndex = Boolean(storedScreens?.payrollIndex);
  const canRead = Boolean(storedScreens?.payrollRead);
  const canCreate = Boolean(storedScreens?.payrollCreate);
  const canUpdate = Boolean(storedScreens?.payrollUpdate);
  const canDelete = Boolean(storedScreens?.payrollDelete);

  const showActionColumn = canUpdate || canDelete;

  const defaultColumnVisibility = {
    bonus: false,
    cpfContributions: false,
    deductionAmount: false,
    grossPay: false,
    payrollMonth: false,
    payrollType: false,
    shgContribution: false,
    freelanceCount: false,
    userId: false,
    year: false,
    startDate: false,
    endDate: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        enableHiding: true,
        header: "Status",
        Cell: ({ row }) =>
          row.original.status === "APPROVED" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#287f71" }}
            >
              Approved
            </span>
          ) : row.original.status === "PENDING" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#eb862a" }}
            >
              Pending
            </span>
          ) : row.original.status === "REJECTED" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#ed1a1a" }}
            >
              Rejected
            </span>
          ) : null,
      },
      {
        accessorKey: "userRole",
        enableHiding: true,
        header: "Role",
        Cell: ({ row }) =>
          row.original.userRole === "SMS_TEACHER" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#287f71" }}
            >
              Teacher
            </span>
          ) : row.original.userRole === "SMS_FREELANCER" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#eb862a" }}
            >
              Freelancer
            </span>
          ) : row.original.userRole === "SMS_STAFF" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#ed1a1a" }}
            >
              Staff
            </span>
          ) : row.original.userRole === "SMS_BRANCH_ADMIN" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#b71c1c" }}
            >
              Branch Admin
            </span>
          ) : row.original.userRole === "SMS_STAFF_ADMIN" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#d32f2f" }}
            >
              Staff Admin
            </span>
          ) : row.original.userRole === "SMS_MARKETING" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#e2bc32" }}
            >
              Marketing
            </span>
          ) : row.original.userRole === "CENTER_MANAGER" ? (
            <span
              className="badge text-light fw-light"
              style={{ backgroundColor: "#c62828" }}
            >
              Center Manager
            </span>
          ) : null,
      },
      // { accessorKey: "centerName", enableHiding: true, header: "Center Name" },
      {
        accessorKey: "employeeName",
        enableHiding: true,
        header: "Employee Name",
      },
      {
        accessorKey: "netPay",
        enableHiding: true,
        header: "Net Pay",
      },
      { accessorKey: "bonus", header: "Bonus" },
      { accessorKey: "cpfContributions", header: "Cpf Contributions" },
      { accessorKey: "deductionAmount", header: "Deduction Amount" },
      { accessorKey: "grossPay", header: "Gross Pay" },
      { accessorKey: "payrollMonth", header: "Payroll Month" },
      { accessorKey: "payrollType", header: "Payroll Type" },
      { accessorKey: "shgContribution", header: "Shg Contribution" },
      { accessorKey: "freelanceCount", header: "Freelance Count" },
      { accessorKey: "userId", header: "User Id" },
      { accessorKey: "year", header: "Year" },
      { accessorKey: "startDate", header: "Start Date" },
      { accessorKey: "endDate", header: "End Date" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    return [
      baseColumns[0], // S.NO
      {
        accessorKey: "id",
        header: "",
        size: 20,
        enableSorting: false,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  const getCenterData = async () => {
    try {
      const centerData = await fetchAllCentersWithIds();
      setCenterData(centerData);
    } catch (error) {
      toast.error(error);
    }
  };

  /* -------------------- DATA FETCH -------------------- */
  const fetchData = async () => {
    try {
      const url =
        selectedCenter === 0 || selectedCenter === "0"
          ? `getAllUserPayroll`
          : `/getAllUserPayroll?centerId=${selectedCenter}`;

      const response = await api.get(url);

      setAllData(response.data);
      setDatas(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data ", error);
    }
  };

  useEffect(() => {
    fetchData();
    getCenterData();
  }, [selectedCenter]);

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data: datas,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, datas, "PayrollList.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: canRead
      ? ({ row }) => ({
          onClick: () => navigate(`/payrolladmin/view/${row.original.id}`),
          style: { cursor: "pointer" },
        })
      : undefined,
  });

  return (
    <div className="container-fluid center my-4">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Staffing
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          Payroll
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="active dot"></div>
            </div>
            <span className="text-muted me-2">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Payroll
              </span>
            </span>
          </div>
        </div>
        <div className="d-flex justify-content-end mb-3 px-2">
          {canCreate && (
            <Link to="/payrolladmin/add">
              <button
                type="button"
                className="btn btn-button btn-sm me-2"
                style={{ fontWeight: "600px !important" }}
              >
                &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
              </button>
            </Link>
          )}
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {showActionColumn && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem
                    onClick={() => navigate(`/payrolladmin/edit/${selectedId}`)}
                    className="text-start mb-0 menuitem-style"
                  >
                    Edit
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem>
                    <GlobalDelete
                      path={`/deleteUserPayroll/${selectedId}`}
                      onDeleteSuccess={fetchData}
                      onOpen={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Payroll;
